/**
 * Payroll runs — the runs LIST (Payroll Increment 2).
 *
 * Mirrors `prototype/payroll/payroll-runs.html`: a 4-up iconed KPI tile strip
 * (Paid this FY · Avg monthly net · On payroll · Next run) over a table of
 * runs (period · status pill · employees · gross · deductions · net · pay
 * date). Rows open the run detail. "Run payroll" creates a draft for the
 * current month then navigates to it.
 *
 * @see prototype/payroll/payroll-runs.html
 * @see docs/product/payroll-v1-build-plan.md §"Inc 2 — Payroll run"
 */
import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ChevronRight, CreditCard, Plus, TrendingUp, UsersRound, CalendarDays } from "lucide-react";
import { Alert, toast } from "@flint/ui";
import { paiseToInr, paiseToInrShort, formatDate } from "../../../lib/format.ts";
import { trpc } from "../../../lib/trpc.ts";
import { WorkspaceShell } from "../../../features/workspace/index.ts";
import { PayrollKpiTiles, periodLabel, statusPill } from "../../../features/payroll/index.ts";
import { DataTableCard, ListPagination } from "../../../features/master-list/index.ts";

export const Route = createFileRoute("/(workspace)/payroll/")({
  component: PayrollRunsListPage,
});

function PayrollRunsListPage() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const PAGE_SIZE = 50;
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const cursor = cursorStack[cursorStack.length - 1];

  const list = trpc.payrollRuns.list.useQuery(
    {
      limit: PAGE_SIZE,
      ...(cursor !== undefined ? { cursor } : {}),
    },
    { placeholderData: (prev) => prev },
  );

  const create = trpc.payrollRuns.create.useMutation({
    onSuccess: async (res) => {
      await utils.payrollRuns.list.invalidate();
      void navigate({ to: "/payroll/$runId", params: { runId: res.id } });
    },
    onError: (err) => {
      // Duplicate-for-period → open the existing run instead of erroring out.
      const cause = err.data?.cause as { reason?: string; existingRunId?: string } | undefined;
      if (cause?.reason === "duplicate-run-for-period" && cause.existingRunId) {
        toast.info("A run already exists for this month — opening it.");
        void navigate({ to: "/payroll/$runId", params: { runId: cause.existingRunId } });
        return;
      }
      toast.error(err.message);
    },
  });

  const rows = list.data?.items ?? [];
  const nextCursor = list.data?.nextCursor;
  const kpis = list.data?.kpis;

  // Pagination derivations — page index = cursor-stack depth + 1.
  const pageNumber = cursorStack.length + 1;
  const showingFrom = rows.length === 0 ? 0 : cursorStack.length * PAGE_SIZE + 1;
  const showingTo = cursorStack.length * PAGE_SIZE + rows.length;
  const handlePrev = () => setCursorStack((s) => s.slice(0, -1));
  const handleNext = () => {
    if (nextCursor) setCursorStack((s) => [...s, nextCursor]);
  };

  const stats = computeRunStats(rows);

  function runPayroll() {
    if (!kpis) return;
    create.mutate({ periodMonth: kpis.nextRunPeriod });
  }

  return (
    <WorkspaceShell breadcrumbs={[{ label: "Payroll" }, { label: "Runs" }]}>
      <div className="space-y-4">
        {/* Head */}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-ink">Payroll runs</h1>
            <p className="mt-0.5 text-sm text-muted">
              {kpis ? `Monthly salary runs · ${kpis.onPayroll} on payroll` : "Monthly salary runs"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/payroll/employees"
              className="inline-flex h-10 items-center rounded-sm border border-line bg-paper px-4 text-sm font-semibold text-ink hover:bg-bg"
            >
              Employees
            </Link>
            <button
              type="button"
              onClick={runPayroll}
              disabled={create.isPending || !kpis}
              className="inline-flex h-10 items-center gap-2 rounded-sm bg-ink px-4 text-sm font-semibold text-white hover:bg-ink-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Plus size={16} aria-hidden />
              {create.isPending ? "Creating…" : "Run payroll"}
            </button>
          </div>
        </div>

        {/* KPI tiles */}
        {kpis ? (
          <PayrollKpiTiles
            tiles={[
              { icon: CreditCard, label: "Paid this FY", value: paiseToInr(kpis.paidThisFyPaise) },
              { icon: TrendingUp, label: "Avg monthly net", value: paiseToInr(kpis.avgMonthlyNetPaise) },
              { icon: UsersRound, label: "On payroll", value: String(kpis.onPayroll) },
              {
                icon: CalendarDays,
                label: kpis.currentPeriodHasRun ? "This month" : "Next run",
                value: periodLabel(kpis.nextRunPeriod),
                net: true,
                text: true,
              },
            ]}
          />
        ) : null}

        {/* Table */}
        {list.error ? (
          <Alert intent="bad" title="Couldn't load runs" description={list.error.message} />
        ) : rows.length === 0 ? (
          <div className="rounded-md border border-dashed border-line bg-paper p-10 text-center">
            <p className="text-sm font-semibold text-ink">No payroll runs yet</p>
            <p className="mt-1 text-sm text-muted">
              Run payroll for the current month — Flint pre-fills each active employee.
            </p>
            <button
              type="button"
              onClick={runPayroll}
              disabled={create.isPending || !kpis}
              className="mt-4 inline-flex h-10 items-center gap-2 rounded-sm bg-ink px-4 text-sm font-semibold text-white hover:bg-ink-2 disabled:opacity-60"
            >
              <Plus size={16} aria-hidden />
              Run payroll
            </button>
          </div>
        ) : (
          <DataTableCard
            footer={[
              { label: "Visible", value: rows.length },
              { label: "Page net", value: paiseToInrShort(stats.sumNetPaise.toString()) },
              { label: "Page gross", value: paiseToInrShort(stats.sumGrossPaise.toString()) },
              { label: "Avg net", value: paiseToInrShort(stats.avgNetPaise.toString()) },
              ...(stats.payDateRange ? [{ label: "Paid", value: stats.payDateRange }] : []),
            ]}
            pagination={
              <ListPagination
                pageNumber={pageNumber}
                showingFrom={showingFrom}
                showingTo={showingTo}
                sortLabel="sorted by period (newest first)"
                hasPrev={cursorStack.length > 0}
                hasNext={!!nextCursor}
                onPrev={handlePrev}
                onNext={handleNext}
              />
            }
          >
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line bg-raised text-left text-xs uppercase tracking-wider text-muted">
                  <th className="px-4 py-3 font-semibold">Period</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 text-right font-semibold">Employees</th>
                  <th className="px-4 py-3 text-right font-semibold">Gross</th>
                  <th className="px-4 py-3 text-right font-semibold">Deductions</th>
                  <th className="px-4 py-3 text-right font-semibold">Net</th>
                  <th className="px-4 py-3 font-semibold">Pay date</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const pill = statusPill(r.status);
                  return (
                    <tr
                      key={r.id}
                      className="cursor-pointer border-b border-line last:border-0 hover:bg-bg"
                      onClick={() => navigate({ to: "/payroll/$runId", params: { runId: r.id } })}
                    >
                      <td className="px-4 py-3 font-semibold text-ink">{periodLabel(r.periodMonth)}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${pill.className}`}
                        >
                          {pill.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-muted">{r.employeeCount}</td>
                      <td className="px-4 py-3 text-right font-mono text-ink">
                        {paiseToInr(r.totalGrossPaise)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-muted">
                        {paiseToInr(r.totalDeductionsPaise)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-ink">
                        {paiseToInr(r.totalNetPaise)}
                      </td>
                      <td className="px-4 py-3 font-mono text-muted">
                        {r.paidAt ? formatDate(r.paidAt) : "—"}
                      </td>
                      <td className="px-4 py-3 text-right text-muted">
                        <ChevronRight size={16} aria-hidden />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </DataTableCard>
        )}
      </div>
    </WorkspaceShell>
  );
}

/** Visible-set aggregates for the stats footer. */
function computeRunStats(
  rows: Array<{ totalNetPaise: string; totalGrossPaise: string; paidAt: string | null }>,
): {
  sumNetPaise: bigint;
  sumGrossPaise: bigint;
  avgNetPaise: bigint;
  payDateRange: string | null;
} {
  if (rows.length === 0) {
    return { sumNetPaise: 0n, sumGrossPaise: 0n, avgNetPaise: 0n, payDateRange: null };
  }
  let sumNet = 0n;
  let sumGross = 0n;
  let min: string | null = null;
  let max: string | null = null;
  for (const r of rows) {
    sumNet += BigInt(r.totalNetPaise);
    sumGross += BigInt(r.totalGrossPaise);
    if (r.paidAt) {
      if (min === null || r.paidAt < min) min = r.paidAt;
      if (max === null || r.paidAt > max) max = r.paidAt;
    }
  }
  let payDateRange: string | null = null;
  if (min !== null && max !== null) {
    payDateRange = min === max ? formatDate(min) : `${formatDate(min)} → ${formatDate(max)}`;
  }
  return {
    sumNetPaise: sumNet,
    sumGrossPaise: sumGross,
    avgNetPaise: sumNet / BigInt(rows.length),
    payDateRange,
  };
}
